import { add, flatMap, map, memoize, omitBy, reduce, pick, values } from "../../utils/lodash";
import { ColumnType } from "../enums/ColumnType";
import { RowType } from "../enums/RowType";
import {
    getSettingBoolean,
    getSettingString,
    getSettingNumber,
    hasSetting,
} from "./settingUtilities";
import { translate } from "./translationUtilities";
import { ChoiceModel } from "../ChoiceModel";
import { QuestionModel } from "../QuestionModel";
import { QuestionnaireModel } from "../QuestionnaireModel";
import { QuestionType } from "../enums/QuestionType";
import { AxisModel } from "../AxisModel";
import { ValidationResultModel } from "../ValidationResultModel";
import { findVariables } from "../../expressions/parser";
import { getAllDisplayLogicVariables } from "../factories/questionFactory";
import { Translatable } from "../Translatable";
import { isNullOrEmpty } from "../../utils";
import { ControlProps } from "../../components/controls/BaseControl";

export const isRowMergeCells = (row: AxisModel) => getSettingBoolean(row, "MergeCells");
export const isRowMergeLeftLabel = (row: AxisModel) => getSettingBoolean(row, "MergeLeftLabel");
export const isRowMergeRightLabel = (row: AxisModel) => getSettingBoolean(row, "MergeRightLabel");
export const isHeaderRow = (row: AxisModel) =>
    [RowType.Text, RowType.Title].includes(row.type as RowType);
export const getUseChoiceTexts = (row: AxisModel) => getSettingBoolean(row, "UseChoiceTexts");
export const shouldShowRowLeftLabel = (row: AxisModel) => !isRowMergeLeftLabel(row);
export const shouldShowRowRightLabel = (row: AxisModel) => !isRowMergeRightLabel(row);
export const isSplitColumn = (column: AxisModel) =>
    [ColumnType.CheckBox, ColumnType.RadioButton, ColumnType.ImageSelector].includes(
        column.type as ColumnType,
    );
export const variableForCell = (row: AxisModel, column: AxisModel) => row.variables[column.index];
export const hasOpenEnd = (row: AxisModel | ChoiceModel) => hasSetting(row, "OpenEndVariable");
export const openEndVariableName = (row: AxisModel | ChoiceModel) =>
    getSettingString(row, "OpenEndVariable");
export const getColumnCount = (columns: AxisModel[]) => {
    const columnCount = map(columns, (c) =>
        isSplitColumn(c)
            ? c.variables.find((v) => v.choiceList?.length > 0)?.choiceList?.length
            : 1,
    );
    return reduce(columnCount, add);
};
export const getVariablesCount = (question: QuestionModel) =>
    flatMap(question.rows, (r) => map(r.variables, (v) => v.name)).length;

export const isSingleChoice = (question: QuestionModel) =>
    getSettingNumber(question, "MaxAllowedAnswers") === 1 ||
    (question.type !== QuestionType.HybridGrid &&
        question.columns.some((c) => c.type === ColumnType.RadioButton));

export const autoAdvanceCarouselQuestion = (question: QuestionModel, props: ControlProps) => {
    const { mentions, sequencedVariables } = props;

    const carouselIsCheckBoxType =
        getSettingString(question, "CarrouselChoiceListAnswerType") ===
        QuestionType[QuestionType.CheckBox];

    const hasMaxAllowedAnswers =
        hasSetting(question, "MaxAllowedAnswers") &&
        getSettingNumber(question, "MaxAllowedAnswers") === 1;

    const answerRequired =
        !hasSetting(question, "AnswerRequired") || getSettingBoolean(question, "AnswerRequired");

    return answerRequired && (!carouselIsCheckBoxType || hasMaxAllowedAnswers)
        ? sequencedVariables.length === flatMap(mentions, (mention) => mention).length &&
              flatMap(mentions, (mention) => mention.length > 0).indexOf(false) <= 0
        : false;
};

export const hasRightLabels = (entitiesWithLabels: Translatable[], language: string) =>
    entitiesWithLabels.some((entity) => !isNullOrEmpty(translate(entity, "RightLabel", language)));

export const hasLeftLabels = (entitiesWithLabels: Translatable[], language: string) =>
    entitiesWithLabels.some(
        (entity) =>
            !isNullOrEmpty(translate(entity, "LeftLabel", language)) ||
            !isNullOrEmpty(translate(entity, "TotalRowLabel", language)),
    );

const choiceNameForCell = (row: AxisModel, column: AxisModel, choice: ChoiceModel) =>
    `${variableForCell(row, column)}_${choice.index + 1}`;

export const choiceForCell = memoize(
    (row: AxisModel, column: AxisModel, choice: ChoiceModel): ChoiceModel => ({
        ...choice,
        name: choiceNameForCell(row, column, choice),
        settings: omitBy(choice.settings, (value, key) => key.startsWith("OpenEnd")),
    }),
    choiceNameForCell,
);

const controlTypes: any = {
    [ColumnType.CheckBox]: QuestionType.CheckBox,
    [ColumnType.RadioButton]: QuestionType.RadioButton,
    [ColumnType.ImageSelector]: QuestionType.ImageSelector,
    [ColumnType.DropDownList]: QuestionType.DropDownList,
    [ColumnType.StarRating]: QuestionType.StarRatingGrid,
    [ColumnType.NumericAnswer]: QuestionType.NumericAnswer,
    [ColumnType.Slider]: QuestionType.Slider,
};

export function questionTypeForColumn(column: AxisModel) {
    return controlTypes[column.type] || QuestionType.TextAnswer;
}

function getRowVariableDependencies(row: AxisModel, questionnaire: QuestionnaireModel) {
    return [
        ...findVariables(row.displayLogic, questionnaire),
        ...findVariables(row.skipLogic, questionnaire),
        ...(row.openEndDisplayIf ? findVariables(row.openEndDisplayIf, questionnaire) : []),
    ];
}

function getColumnVariableDependencies(column: AxisModel, questionnaire: QuestionnaireModel) {
    return [
        ...findVariables(column.displayLogic, questionnaire),
        ...findVariables(column.skipLogic, questionnaire),
        ...getAllDisplayLogicVariables(column.variables, questionnaire),
    ];
}

function getCellVariableDependencies(row: AxisModel, column: AxisModel, questionnaire: QuestionnaireModel) {
    return [...getRowVariableDependencies(row, questionnaire), ...getColumnVariableDependencies(column, questionnaire)];
}

export const questionForCell = (
    question: QuestionModel,
    row: AxisModel,
    column: AxisModel,
    questionnaire: QuestionnaireModel,
): QuestionModel => {
    const variables = getCellVariables(row, column, question);
    return {
        id: 0,
        parent: question,
        name: variableForCell(row, column).name,
        settings: {
            ...column.settings,
            MaxAllowedAnswers:
                column.settings["MaxAllowedAnswers"] ||
                (question.settings && question.settings["MaxAllowedAnswers"]),
            AnswerRequired: !(
                (hasSetting(row, "AnswerRequired") && !getSettingBoolean(row, "AnswerRequired")) ||
                (hasSetting(column, "AnswerRequired") &&
                    !getSettingBoolean(column, "AnswerRequired"))
            ),
        },
        translatedTexts: column.translatedTexts,
        type: questionTypeForColumn(column),
        elementId: variableForCell(row, column).name,
        index: column.index,
        blockIndex: question.blockIndex,
        variables,
        skipLogic: column.skipLogic,
        displayLogic: column.displayLogic,
        isPageBreak: false,
        variableNames: variables.map((v) => v?.name),
        variableDependencies: getCellVariableDependencies(row, column, questionnaire),
    };
};

const getCellVariables = (row: AxisModel, column: AxisModel, question: QuestionModel) => {
    const variables = [variableForCell(row, column)];
    const openEndVarName = openEndVariableName(row);
    if (openEndVarName) {
        const openEndVar = getQuestionVariableWithName(question, openEndVarName);
        variables.push(openEndVar);
    }
    return variables;
};

const getQuestionVariableWithName = (question: QuestionModel, variableName: string) =>
    question.variables?.find((v) => v.name === variableName);

export const rowHasError = (
    row: AxisModel,
    columns: AxisModel[],
    validationResult: ValidationResultModel,
) => {
    if (!row || row.type !== RowType.Variable || !validationResult) return false;

    const rowVariables = columns.map((column) => variableForCell(row, column));
    const errors = pick(
        validationResult.errorsByVariableName,
        rowVariables.map((v) => v.name),
    );
    return values(errors).length > 0;
};

export const cellHasError = (
    row: AxisModel,
    column: AxisModel,
    validationResult: ValidationResultModel,
) => {
    if (!row || row.type !== RowType.Variable || !validationResult || !column) return false;

    const variable = variableForCell(row, column);
    const errors = validationResult.errorsByVariableName[variable.name] || [];
    return errors && errors.length > 0;
};
